// Device-scoped record of native recordings the identity guard REFUSED at handoff.
//
// The native shell keeps a refused recording ("error" keeps the audio) and later
// re-offers it through the stored-replay path (usePostMessages `syncRecording`),
// which carries no start time and no patient over the bridge. Without this record the
// replay would upload it unchecked, so the refusal is remembered here in localStorage,
// addressable by the recording uuid OR the native row id and deliberately NOT filtered
// by the signed-in user (a different user on the same phone must not bypass it). The
// replay re-judges the recording with the identity captured at record time. A stored
// refusal only restricts an upload; it never grants one.
//
// All storage access is guarded; on locked-down browsers the feature is silently off.
export const NATIVE_IDENTITY_REFUSALS_KEY = "nativeIdentityRefusals";
const MAX_ENTRIES = 128;

const read = () => {
  try {
    const entries = JSON.parse(localStorage.getItem(NATIVE_IDENTITY_REFUSALS_KEY) || "[]");
    return Array.isArray(entries) ? entries.filter((e) => e && e.userId && typeof e.recordingUuid === "string") : [];
  } catch (_) {
    return [];
  }
};

const write = (entries) => {
  try {
    localStorage.setItem(NATIVE_IDENTITY_REFUSALS_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
    return true;
  } catch (_) {
    return false;
  }
};

// An entry is addressable by its recording uuid OR the native row id: the handoff and the
// stored replay do not always carry the same one (old shells send only the row id).
const matches = (entry, key) => entry.recordingUuid === String(key) || (entry.recordingId != null && entry.recordingId === String(key));

// entry: { recordingUuid, recordingId (native row id, optional), startedAt, patientUserId, encounterUuid, chatId }
export function rememberIdentityRefusal(userId, entry) {
  if (!userId || !entry || entry.recordingUuid == null || entry.recordingUuid === "") return false;
  const recordingUuid = String(entry.recordingUuid);
  const recordingId = entry.recordingId == null || entry.recordingId === "" ? null : String(entry.recordingId);
  const kept = read().filter((e) => !matches(e, recordingUuid) && !(recordingId != null && matches(e, recordingId)));
  kept.push({
    userId: String(userId),
    recordingUuid,
    recordingId,
    startedAt: entry.startedAt ?? null,
    patientUserId: entry.patientUserId ?? null,
    encounterUuid: entry.encounterUuid ?? null,
    chatId: entry.chatId ?? null,
  });
  return write(kept);
}

// The first argument (signed-in user) is accepted for call-site compatibility but NOT used
// to filter: the record only restricts an upload and grants nothing, so any user on this
// device re-judges a refused recording the same way.
export function findIdentityRefusal(_userId, recordingKey) {
  if (recordingKey == null || recordingKey === "") return null;
  return read().find((e) => matches(e, recordingKey)) || null;
}

export function forgetIdentityRefusal(_userId, recordingKey) {
  if (recordingKey == null || recordingKey === "") return false;
  return write(read().filter((e) => !matches(e, recordingKey)));
}
